import { useCallback, useEffect, useRef, useState } from "react";
import { WidgetPanel } from "components/widgets/WidgetPanel";
import { usePlaybackSession } from "connections/playbackSession";
import { Colors } from "design/colors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlugCircleExclamation, faPlugCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { useDevices } from "hooks/useDevices";
import { FileExport } from "components/navbar/ExportButton";
import { clearPlaybackState } from "hooks/usePlayback";

export const Dashboard = ( props: {
    loadDevicesScreen: () => void;
} ) => {
    const inputRef = useRef<HTMLInputElement>( null );
    const { playbackSession, startSession } = usePlaybackSession();
    const { connectedDevice, triggerDisconnectExport, onDisconnect} = useDevices();
    const [ showExport, setShowExport ] = useState(false);

    /* Add event listener for HID disconnect to Dashboard instead of useDevices hook
     * and call onDisconnect from useDevices hook to avoid circular dependency. Calling the hook directly
     * allows the hook to update the triggerDisconnectExport state variable which is used to trigger the
     * export dialog.
     */
    if ( navigator['hid'] ) {
      const cb = ( e: HIDConnectionEvent ) => {
        // TODO: see if we could somehow use the useReset hook here.
        onDisconnect(e);
        clearPlaybackState();
      }
      ( navigator.hid.ondisconnect as ( e: HIDConnectionEvent ) => void ) = cb;
    }

    useEffect(() => {
        if (triggerDisconnectExport) {
            setShowExport(true);
        }
    }, [triggerDisconnectExport]);

    const handleInputChange = useCallback( async ( e: React.FormEvent<HTMLInputElement> ) => {
        const files = e.currentTarget.files;
        if ( !isDefined( files ) || files.length === 0 ) {
            return;
        }

        const file = files[ 0 ];
        await startSession({ file });

        if ( inputRef.current ) {
            inputRef.current.value = "";
        }
    }, [ startSession ] );


    if ( connectedDevice === undefined && !playbackSession?.isPlayback ) {
        return (
            <>
                <input ref={ inputRef } style={{ display: "none" }} type="file" accept=".slnt" onInput={ handleInputChange } />
                <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", color: Colors.WHITE }}>
                    <FontAwesomeIcon
                        icon={ faPlugCircleXmark }
                        size="5x"
                        color={ Colors.WHITE }
                        style={{ marginBottom: 32 }}
                    />
                    <div style={{ fontSize: 24, fontWeight: "bold", marginBottom: 16 }}>Not connected.</div>
                    <div style={{ fontSize: 16 }}>
                        <span style={{ color: Colors.LIGHT_LIGHT_GREEN, cursor: "pointer" }} onClick={ props.loadDevicesScreen }>
                            Connect a controller
                        </span> or&nbsp;
                        <span style={{ color: Colors.LIGHT_LIGHT_GREEN, cursor: "pointer" }} onClick={ () => inputRef?.current?.click() }>
                            load a playback file
                        </span>.
                    </div>
                </div>
                { showExport && <FileExport onClose={ () => setShowExport(false) } /> }
            </>
        );
    } else if ( connectedDevice !== undefined && connectedDevice.initialized && (connectedDevice.cstore === undefined || connectedDevice.cstoreErr !== undefined)) {
        return (
            <div style={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", color: Colors.WHITE }}>
                <FontAwesomeIcon
                    icon={ faPlugCircleExclamation }
                    size="5x"
                    color={ Colors.YELLOW }
                    style={{ marginBottom: 32 }}
                />
                <div style={{ fontSize: 24, marginBottom: 16 }}> Your <span style={{ fontWeight: "bold" }}>{ connectedDevice.handle.productName }</span>  has a missing or invalid configuration store.</div>
                <div style={{ fontSize: 16, color: Colors.LIGHT_LIGHT_GREEN, cursor: "pointer" }} onClick={ () => props.loadDevicesScreen() }> Update Firmware and/or Config</div>
            </div>
        );
    }
    return ( <WidgetPanel /> );
}
